// What every Definable host agrees on about the Stytch session: the dashboard, the consumer app
// and the marketing site, whose sign-in page reads this file too (marketing-site/src/auth). One
// cookie on the parent domain makes a sign-in on one host a sign-in on all of them. No imports:
// the site's build has this folder's source but not its node_modules.

// 7 days (00-PLAN.md). The session JWT itself stays 5 minutes; the SDK refreshes it.
export const SESSION_DURATION_MINUTES = 10080;

const DEFAULT_COOKIE = "stytch_session";

/** The session cookie's name. dev.definable.ai sits under definable.ai, so production's cookie
    reaches dev's hosts too: a name per domain keeps the two sessions apart. */
export function sessionCookieName(domain: string | undefined): string {
  return domain ? `definable_session_${domain.replace(/\W/g, "_")}` : DEFAULT_COOKIE;
}

/** Stytch's `cookieOptions` for `domain`, the parent domain every host sits under
    (`AUTH_COOKIE_DOMAIN`, dev.definable.ai on dev). Unset keeps the cookie on its own host,
    which is what localhost wants: its ports share cookies anyway.
    The SDK writes the cookie from JavaScript, so it can't be HttpOnly: every host under
    `domain` can read the session. Only a domain whose hosts are all ours and serve nothing a
    user or an agent controls qualifies. definable.ai doesn't yet: sandbox previews
    (*.proxy.sandbox.definable.ai) serve whatever an agent runs, and docs is a third party. */
export function sessionCookieOptions(domain: string | undefined) {
  if (!domain) return undefined;
  const name = sessionCookieName(domain);
  return { availableToSubdomains: true, domain, opaqueTokenCookieName: name, jwtCookieName: `${name}_jwt` };
}

/** True when the session cookie is set but this origin's SDK holds no session: the person
    signed in on another Definable host. Stytch keeps the session itself per origin, so the
    SDK has to fetch it with the cookie before anyone can tell they are signed in. */
export function signedInElsewhere(domain: string | undefined, hasLocalSession: boolean): boolean {
  if (hasLocalSession || typeof document === "undefined") return false;
  const name = `${sessionCookieName(domain)}=`;
  return document.cookie.split("; ").some((cookie) => cookie.startsWith(name));
}
